import { useState, useEffect } from "react";
import { ThumbsUp, ThumbsDown, Loader2, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/shadcn/dialog";
import { Button } from "@/components/shadcn/button";
import { feedbackApi } from "@/api/feedback";
import { MarkdownRenderer } from "@/components/chat/MarkdownRenderer";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";

interface SummaryDialogProps {
    isOpen: boolean;
    onClose: () => void;
    summaryId: number;
    summary: string;
    entityType: string;
    title: string;
    subtitle?: React.ReactNode;
    regenerateFn: () => Promise<{ id: number; summary: string }>;
    onSummaryUpdated?: (newSummary: string, newSummaryId: number) => void;
}

export function SummaryDialog({
    isOpen,
    onClose,
    summaryId,
    summary,
    entityType,
    title,
    subtitle,
    regenerateFn,
    onSummaryUpdated,
}: SummaryDialogProps) {
    const [userFeedback, setUserFeedback] = useState<number | null>(null);
    const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);

    useEffect(() => {
        setUserFeedback(null);
    }, [summaryId]);

    const handleFeedback = async (rating: number) => {
        if (userFeedback !== null) return;

        setIsSubmittingFeedback(true);
        try {
            await feedbackApi.submitFeedback({
                entityType,
                entityId: summaryId.toString(),
                feedbackValue: rating === 1,
            });
            setUserFeedback(rating);
        } catch (error) {
            logger.error("Failed to submit feedback:", error);
        } finally {
            setIsSubmittingFeedback(false);
        }
    };

    const handleRegenerate = async () => {
        setIsRegenerating(true);
        try {
            const response = await regenerateFn();
            onSummaryUpdated?.(response.summary, response.id);
            setUserFeedback(null);
        } catch (error) {
            logger.error("Failed to regenerate summary:", error);
        } finally {
            setIsRegenerating(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="w-[calc(100%-2rem)] max-w-3xl max-h-[90vh] overflow-y-auto bg-surface-container-lowest border border-outline-variant/20 shadow-[0_30px_60px_rgba(5,0,102,0.06)]">
                <DialogHeader>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="flex-1">
                            <DialogTitle className="text-xl font-headline font-bold text-on-surface mb-2">
                                {title}
                            </DialogTitle>
                            {subtitle && <div className="text-sm text-on-surface-variant">{subtitle}</div>}
                        </div>
                        <Button
                            onClick={handleRegenerate}
                            disabled={isRegenerating}
                            variant="outlined"
                            size="sm"
                            className="gap-1.5 shrink-0 self-start"
                        >
                            {isRegenerating ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <RefreshCw className="w-4 h-4" />
                            )}
                            <span>{isRegenerating ? "Regenerating..." : "Regenerate"}</span>
                        </Button>
                    </div>
                </DialogHeader>

                <div className="mt-4">
                    <div className="prose prose-invert max-w-none">
                        <MarkdownRenderer content={summary} />
                    </div>
                </div>

                <div className="mt-6 pt-4 border-t border-outline-variant/20">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <p className="text-sm text-on-surface-variant">Was this summary helpful?</p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => handleFeedback(1)}
                                disabled={isSubmittingFeedback || userFeedback !== null}
                                className={cn(
                                    "p-2 rounded-md transition-all flex items-center gap-1.5",
                                    userFeedback === 1
                                        ? "bg-green-600/20 text-green-400"
                                        : "hover:bg-surface-container text-on-surface-variant hover:text-green-400",
                                    "disabled:opacity-50 disabled:cursor-not-allowed"
                                )}
                                title="Helpful"
                            >
                                {isSubmittingFeedback && userFeedback === null ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <ThumbsUp className="w-4 h-4" />
                                )}
                                <span className="text-sm">Yes</span>
                            </button>
                            <button
                                onClick={() => handleFeedback(0)}
                                disabled={isSubmittingFeedback || userFeedback !== null}
                                className={cn(
                                    "p-2 rounded-md transition-all flex items-center gap-1.5",
                                    userFeedback === 0
                                        ? "bg-red-600/20 text-red-400"
                                        : "hover:bg-surface-container text-on-surface-variant hover:text-red-400",
                                    "disabled:opacity-50 disabled:cursor-not-allowed"
                                )}
                                title="Not helpful"
                            >
                                {isSubmittingFeedback && userFeedback === null ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <ThumbsDown className="w-4 h-4" />
                                )}
                                <span className="text-sm">No</span>
                            </button>
                        </div>
                    </div>
                    {userFeedback !== null && (
                        <p className="text-xs text-green-400 mt-2">Thank you for your feedback!</p>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
